/**
 * Delete confirmation.
 *
 * Shows the message being deleted, because the office thread's action bar is a hover
 * target on a dense list and "which one did I just click?" is a real question. The
 * server deletes attachments, link previews, unread markers and any time preference or
 * proposal the message carried — hence "cannot be undone" rather than a soft warning.
 */

import {
  BlockScroll,
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "@birdwatch/ui";
import { toast } from "@birdwatch/ui/toast";
import { LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button/button";
import { Markdown } from "@/components/ui/markdown";
import { Message } from "@/components/ui/message";
import type { ChannelMessage } from "../_api/channel-endpoints";
import { ChannelAuthorAvatar, ChannelAuthorName } from "./channel-author";

type ChannelMessageDeleteDialogProps = {
  message: ChannelMessage | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isDeleting: boolean;
  onConfirm: (messageId: string) => Promise<unknown>;
  deleteConfirm?: { title?: string; description?: string };
};

export function ChannelMessageDeleteDialog({
  message,
  open,
  onOpenChange,
  isDeleting,
  onConfirm,
  deleteConfirm,
}: ChannelMessageDeleteDialogProps) {
  if (!message) return null;

  const title = deleteConfirm?.title ?? "Delete message";
  const description =
    deleteConfirm?.description ??
    "Are you sure you want to delete this message? This cannot be undone.";

  const handleConfirm = async () => {
    try {
      await onConfirm(message.id);
      onOpenChange(false);
      toast.success({ title: "Message deleted!" });
    } catch (error) {
      toast.error({ title: "Can't delete message!" });
      console.error(error);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        dialogProps={{ onOpenAutoFocus: (event) => event.preventDefault() }}
        className="sm:max-w-md flex flex-col gap-0 p-0"
      >
        <ModalHeader className="text-left">
          <ModalTitle className="p-0">{title}</ModalTitle>
          <ModalDescription className="sr-only">{title}</ModalDescription>
        </ModalHeader>
        <BlockScroll>
          <div className="p-4 pt-1 space-y-4">
            <p className="text-sm text-text-dark-high">{description}</p>
            <Message
              avatar={<ChannelAuthorAvatar userId={message.userCreatedId} />}
              userCreateName={
                <ChannelAuthorName userId={message.userCreatedId} />
              }
              viaClient={message.viaClient ?? ""}
              createdAt={message.createdAt}
              updatedAt={message.updatedAt}
              className="px-3 border border-border-dark-low rounded-2xl"
            >
              <Markdown
                content={message.body ?? ""}
                className="text-text-dark-max line-clamp-2"
              />
            </Message>
          </div>
        </BlockScroll>
        <ModalFooter>
          <ModalClose asChild>
            <Button type="button" kind="outline" variant="neutral">
              Cancel
            </Button>
          </ModalClose>
          <Button
            variant="negative"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting && <LoaderCircle className="animate-spin" />}
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
